import { useEffect, useState } from "react";

import FeatureGates from "@atlassian/feature-gate-js-client";
import { useActivityClient, ActivityItem } from "@atlassian/recent-work-client";
import { MeResponse } from "@atlassiansox/asc-stargate";

import {
    AisSitesType,
    AvailableProductsResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src/index";

import { useLoggedInUser } from "../../../../../src/utils/logged-in-user-provider";

const GRAPHQL_GATEWAY_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com/gateway/api/graphql"
        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

export enum AtlSiteType {
    CONFLUENCE = "confluence.ondemand",
    JIRA = "jira-software.ondemand"
}

export interface useLogMeInProps {
    availableProducts: AvailableProductsResponse | null;
    userProfile: MeResponse | null;
    siteType: AtlSiteType;
}

export interface useLogMeInResponse {
    userProfileName: string | undefined;
    recentSites: AisSitesType[];
    shouldShowLogMeIn: boolean;
}

export const filterAvailableSites = (
    availableProducts: AvailableProductsResponse | null,
    siteType: AtlSiteType
): AisSitesType[] => {
    if (availableProducts) {
        const filteredSites =
            availableProducts.sites.filter((site) => {
                return site.products.includes(siteType);
            }) || [];

        return filteredSites;
    } else {
        return [];
    }
};

export const constructLoginUrl = (url: string, siteType: AtlSiteType) => {
    const loginUrl = new URL(url);
    switch (siteType) {
        case AtlSiteType.JIRA:
            loginUrl.pathname = "/login.jsp";
            const searchParam = new URLSearchParams();
            const browseUrl = new URL(loginUrl);
            browseUrl.pathname = "/browse";
            searchParam.append("os_destination", browseUrl.href);
            loginUrl.search = searchParam.toString();
            return loginUrl;
            break;
        case AtlSiteType.CONFLUENCE:
            loginUrl.pathname = "/wiki";
            loginUrl.search = "";
            return loginUrl;
            break;
        default:
            return loginUrl;
            break;
    }
};

export const useLogMeIn = ({
    availableProducts,
    userProfile,
    siteType = AtlSiteType.CONFLUENCE
}: useLogMeInProps): useLogMeInResponse => {
    const [filteredSites, setFilteredSites] = useState(
        filterAvailableSites(availableProducts, siteType)
    );
    const [hasCalledAvailableProducts, setHasCalledAvailableProducts] =
        useState(false);

    const loggedInUserName = useLoggedInUser().name;
    const userProfileName = userProfile?.name || loggedInUserName;

    const stargateClient = useBxpWebStargateClient();
    const activityClient = useActivityClient(
        "v3",
        "www.atlassian.com",
        undefined,
        GRAPHQL_GATEWAY_URL
    );

    useEffect(() => {
        const promises = [];
        let mostRecentActivity: ActivityItem | undefined;

        // only make these calls if we have a userProfile, using name as a hint
        if (userProfileName) {
            // Fetch available-products if not available from SSR
            // only call it once though
            if (!filteredSites.length && !hasCalledAvailableProducts) {
                promises.push(
                    stargateClient
                        .availableProducts({})
                        .then((data) => {
                            setFilteredSites(
                                filterAvailableSites(data, siteType)
                            );
                            setHasCalledAvailableProducts(true);
                        })
                        .catch((error) => console.log(error))
                );
            }

            // Fetch recent activity only if we know the user is logged in and has a name
            const fetchActivitiesPromise = activityClient
                .fetchActivities(
                    [50, 125, 250], // retries
                    {
                        limit: 5,
                        products:
                            siteType === AtlSiteType.CONFLUENCE
                                ? ["confluence"]
                                : ["jira"]
                    },
                    ["workedOn", "viewed"]
                )
                .then((data) => {
                    // Which is more recent? The newest viewed or the newest worked on object?
                    const mostRecentlyViewed = data.viewed[0];
                    const mostRecentlyWorkedOn = data.workedOn[0];

                    if (mostRecentlyViewed && mostRecentlyWorkedOn) {
                        mostRecentActivity =
                            new Date(mostRecentlyViewed.timestamp) >
                            new Date(mostRecentlyWorkedOn.timestamp)
                                ? mostRecentlyViewed
                                : mostRecentlyWorkedOn;
                    } else if (mostRecentlyWorkedOn) {
                        mostRecentActivity = mostRecentlyWorkedOn;
                    } else if (mostRecentlyViewed) {
                        mostRecentActivity = mostRecentlyViewed;
                    }
                })
                .catch((error) => console.log(error));
            promises.push(fetchActivitiesPromise);
        }

        // Set state after receiving all responses
        Promise.allSettled(promises).then(() => {
            if (filteredSites.length > 1) {
                if (mostRecentActivity) {
                    const loginUrl = constructLoginUrl(
                        mostRecentActivity.object.url,
                        siteType
                    );

                    const recentSiteIndex = filteredSites.findIndex(
                        (site) => loginUrl.host === new URL(site.url).host
                    );

                    // only move item to the front if not already there or if it was found
                    if (recentSiteIndex > 0) {
                        setFilteredSites([
                            filteredSites[recentSiteIndex] as AisSitesType,
                            ...filteredSites.filter(
                                (site: AisSitesType) =>
                                    new URL(site.url).host !== loginUrl.host
                            )
                        ]);
                    }
                }
            }
        });
    }, [
        stargateClient,
        activityClient,
        filteredSites,
        siteType,
        hasCalledAvailableProducts,
        userProfileName
    ]);

    let shouldShowLogMeIn = !!userProfileName && filteredSites.length > 0;

    if (shouldShowLogMeIn && siteType === AtlSiteType.CONFLUENCE) {
        // only expose the user if they are logged in and have recentSites
        const logMeInEnrollment = FeatureGates.getExperimentValue(
            "cfind-1109-confluence-log-me-back-in",
            "enrolled",
            false
        );
        shouldShowLogMeIn = shouldShowLogMeIn && logMeInEnrollment;
    }

    return { userProfileName, recentSites: filteredSites, shouldShowLogMeIn };
};
